import { AuthorizationRequestHandler, BasicQueryStringUtils, DefaultCrypto, AuthorizationServiceConfiguration, AuthorizationRequest, AuthorizationResponse, AuthorizationError, } from '@openid/appauth';
/**
 * Sourced from RedirectBasedHandler at https://github.com/openid/AppAuth-JS/blob/cf6bb68dfe630c5d5f415bdcc76ea2581f041d8f/src/redirect_based_handler.ts
 *
 * Some customizations were made including:
 *   Remove LocalStorage abstraction so calls can be synchronous.
 *   completeAuthorizationRequestSync - This was customized to include the AuthorizationServiceConfiguration so we don't
 *      need to make an additional network request when logging the user in. We also clear the hash after capturing it.
 *
 */
/** key for authorization request. */
export const authorizationRequestKey = (handle) => {
    return `${handle}_STREEM_AUTH_REQUEST`;
};
/** key for authorization service configuration */
export const authorizationServiceConfigurationKey = (handle) => {
    return `${handle}_STREEM_AUTH_CONFIG`;
};
/** key in local storage which represents the current authorization request. */
export const AUTHORIZATION_REQUEST_HANDLE_KEY = 'STREEM_CURRENT_AUTH_REQUEST';
/**
 * Represents an AuthorizationRequestHandler which uses a standard
 * redirect based code flow.
 */
export class RedirectRequestHandler extends AuthorizationRequestHandler {
    constructor(utils = new BasicQueryStringUtils(), crypto = new DefaultCrypto()) {
        super(utils, crypto);
    }
    async performAuthorizationRequest(configuration, request) {
        const handle = this.crypto.generateRandom(10);
        // before you make request, persist all request related data in local storage.
        const requestJson = await request.toJson();
        window.localStorage.setItem(AUTHORIZATION_REQUEST_HANDLE_KEY, handle);
        window.localStorage.setItem(authorizationRequestKey(handle), JSON.stringify(requestJson));
        window.localStorage.setItem(authorizationServiceConfigurationKey(handle), JSON.stringify(configuration.toJson()));
        const url = this.buildRequestUrl(configuration, request);
        window.location.assign(url);
    }
    pendingAuthorizationRequest() {
        const handle = window.localStorage.getItem(AUTHORIZATION_REQUEST_HANDLE_KEY);
        return typeof handle === 'string' && handle.length > 0;
    }
    /**
     * Attempts to introspect the contents of storage backend and completes the
     * request.
     */
    completeAuthorizationRequestSync() {
        const handle = window.localStorage.getItem(AUTHORIZATION_REQUEST_HANDLE_KEY);
        if (handle) {
            const request = new AuthorizationRequest(JSON.parse(window.localStorage.getItem(authorizationRequestKey(handle)) ?? ''));
            const serviceConfiguration = new AuthorizationServiceConfiguration(JSON.parse(window.localStorage.getItem(authorizationServiceConfigurationKey(handle)) ?? ''));
            const queryParams = this.utils.parse(window.location, true /* use hash */);
            const state = queryParams['state'];
            const code = queryParams['code'];
            const error = queryParams['error'];
            const shouldNotify = state === request.state;
            let authorizationResponse = null;
            let authorizationError = null;
            if (shouldNotify) {
                if (error) {
                    // get additional optional info.
                    const errorUri = queryParams['error_uri'];
                    const errorDescription = queryParams['error_description'];
                    authorizationError = new AuthorizationError({
                        error: error,
                        error_description: errorDescription,
                        error_uri: errorUri,
                        state: state,
                    });
                }
                else {
                    authorizationResponse = new AuthorizationResponse({
                        code: code,
                        state: state,
                    });
                }
                // cleanup state
                window.location.hash = '';
                window.localStorage.removeItem(AUTHORIZATION_REQUEST_HANDLE_KEY);
                window.localStorage.removeItem(authorizationRequestKey(handle));
                window.localStorage.removeItem(authorizationServiceConfigurationKey(handle));
                return {
                    request: request,
                    response: authorizationResponse,
                    serviceConfiguration,
                    error: authorizationError,
                };
            }
        }
        return null;
    }
    completeAuthorizationRequest() {
        return Promise.resolve(this.completeAuthorizationRequestSync());
    }
}
//# sourceMappingURL=redirect_based_handler.js.map